import type { AppState, TerminalResult } from '../state/store';
import type { EffectiveRunStats, EngineEvent, EquipSlot, InventoryItem, ItemAffix, ItemSlotType, RunFoundItem, RunState, SupplyKey } from '../api/types';
import { buildGrid, LEGEND, type Tile } from '../game/dungeon';
import { adjacentEnemies, canDescend, canExtract, canMove, pickupHere } from '../game/runActions';
import { affixSummary, compatibleSlots, describeItem, describeRunItem, formatItemEffect, slotLabel } from '../inventory/format';
import { ENTITY_ASSETS, ENVIRONMENT_TILESETS, ITEM_PICKUP_ASSETS } from '../assets/registry';
import { btn, esc, pill, row } from './components';
import { iconRow, itemIcon } from './icons';

function topMarker(tile: Tile): string {
  return tile.markers[0] ?? 'empty';
}

interface RunLegendEntry {
  label: string;
  /** Sprite URL from the active asset registry (drawn pixelated at icon size). */
  iconUrl: string;
  /** True for spritesheets: crop the first frame instead of showing the whole strip. */
  sheet?: boolean;
}

/** What the run-world assets mean, using the currently active registry art. */
export function runLegendEntries(): RunLegendEntry[] {
  const markers = ENVIRONMENT_TILESETS.catacombs_001.markerProps ?? {
    ferryStep: '/assets/sprites/environment/props/prop_ferry_step_01.png',
    descentStair: '/assets/sprites/environment/props/prop_descent_stair_01.png',
  };
  return [
    { label: 'Item', iconUrl: ITEM_PICKUP_ASSETS.itemChest },
    { label: 'Next floor', iconUrl: markers.descentStair },
    { label: 'Extraction', iconUrl: markers.ferryStep },
    { label: 'Gold', iconUrl: ITEM_PICKUP_ASSETS.goldCache },
    { label: 'Monster', iconUrl: ENTITY_ASSETS.monster_001!.imageUrl, sheet: true },
  ];
}

function runLegendHtml(): string {
  const entries = runLegendEntries()
    .map(({ label, iconUrl, sheet }) => {
      // The text label always renders, so a missing/broken sprite degrades to a
      // compact text legend instead of an empty gap.
      const icon = sheet
        ? `<span class="run-legend__icon run-legend__icon--sheet" style="background-image:url('${esc(iconUrl)}')" aria-hidden="true"></span>`
        : `<img class="run-legend__icon" src="${esc(iconUrl)}" alt="" aria-hidden="true" loading="lazy" decoding="async" />`;
      return `<span class="run-legend__entry">${icon}<span>${esc(label)}</span></span>`;
    })
    .join('');
  return `<div class="run-legend" aria-label="Map legend">${entries}</div>`;
}

function gridHtml(state: RunState): string {
  const grid = buildGrid(state);
  const cells = grid
    .map((line) =>
      line
        .map((tile) => `<div class="tile tile--${esc(topMarker(tile))}" title="${esc(`(${tile.x},${tile.y}) ${tile.label}`)}">${esc(tile.glyph)}</div>`)
        .join(''),
    )
    .join('');
  return `<div class="dungeon" style="grid-template-columns:repeat(${state.floorState.width}, 2.2rem)">${cells}</div>`;
}

function gameFeelStageHtml(): string {
  return [
    '<div class="gamefeel-stage-wrap">',
    '<canvas class="gamefeel-stage" data-gamefeel-stage width="640" height="360" aria-label="Animated dungeon stage"></canvas>',
    '</div>',
  ].join('');
}

function debugPanelsHtml(state: RunState, devMode: boolean): string {
  const debugGrid = gridHtml(state);
  const preview = devMode ? devPreviewPanelHtml() : '';
  return [
    '<details class="text-grid-debug">',
    '<summary>Text grid fallback/debug</summary>',
    debugGrid,
    '<div class="legend">' + LEGEND.map((l) => '<span><code>' + esc(l.glyph) + '</code> ' + esc(l.label) + '</span>').join('') + '</div>',
    '</details>',
    preview,
  ].join('');
}

function devPreviewPanelHtml(): string {
  return [
    '<details class="gamefeel-panel" data-gamefeel-panel>',
    '<summary>DEV animation/SFX preview</summary>',
    '<div class="gamefeel-panel__grid">',
    '<div class="gamefeel-panel__controls">',
    '<label>Entity <select data-gf-preview-entity></select></label>',
    '<label>State <select data-gf-preview-state></select></label>',
    '<div class="btn-row">',
    '<button type="button" class="btn btn--ghost" data-gf-action="preview-play">Play</button>',
    '<button type="button" class="btn btn--ghost" data-gf-action="preview-pause">Pause</button>',
    '<button type="button" class="btn btn--ghost" data-gf-action="preview-restart">Restart</button>',
    '</div>',
    '<label>SFX <select data-gf-sfx></select></label>',
    '<div class="btn-row"><button type="button" class="btn btn--ghost" data-gf-action="sfx-play">Trigger SFX</button></div>',
    '<label class="checkline"><input type="checkbox" data-gf-muted /> Muted</label>',
    '<label>Master <input type="range" min="0" max="1" step="0.05" data-gf-master-volume /></label>',
    '<label>SFX volume <input type="range" min="0" max="1" step="0.05" data-gf-sfx-volume /></label>',
    '<div class="btn-row">',
    '<button type="button" class="btn btn--ghost" data-gf-action="flash">Hit flash</button>',
    '<button type="button" class="btn btn--ghost" data-gf-action="knockback">Knockback</button>',
    '<button type="button" class="btn btn--ghost" data-gf-action="shake">Camera shake</button>',
    '<button type="button" class="btn btn--ghost" data-gf-action="text">Floating text</button>',
    '<button type="button" class="btn btn--ghost" data-gf-action="sparkle">Sparkle</button>',
    '<button type="button" class="btn btn--ghost" data-gf-action="pulse-extract">Extract pulse</button>',
    '<button type="button" class="btn btn--ghost" data-gf-action="pulse-descend">Descend pulse</button>',
    '<button type="button" class="btn btn--ghost" data-gf-action="vfx-slash">Slash VFX</button>',
    '<button type="button" class="btn btn--ghost" data-gf-action="vfx-hit-impact">Hit impact VFX</button>',
    '<button type="button" class="btn btn--ghost" data-gf-action="vfx-death-puff">Death puff VFX</button>',
    '<button type="button" class="btn btn--ghost" data-gf-action="vfx-pickup-sparkle">Pickup sparkle VFX</button>',
    '<button type="button" class="btn btn--ghost" data-gf-action="vfx-extraction-seal">Extraction seal VFX</button>',
    '</div>',
    '</div>',
    '<div>',
    '<canvas class="gamefeel-preview-canvas" data-gf-preview-canvas width="180" height="140" aria-label="Animation preview"></canvas>',
    '<pre class="gamefeel-readout" data-gf-readout>loading preview…</pre>',
    '</div>',
    '</div>',
    '</details>',
  ].join('');
}

function effectiveStats(state: RunState): EffectiveRunStats {
  const supplyEffects = state.config?.supplies?.effects ?? { oilFuel: 5, torchFuel: 3, potionHeal: 4 };
  const oilRestoreBonus = state.oilRestoreBonus ?? 0;
  const torchRestoreBonus = state.torchRestoreBonus ?? 0;
  const potionHealBonus = state.potionHealBonus ?? 0;
  return state.effectiveStats ?? {
    hpCurrent: state.hp,
    hpMax: state.maxHp,
    lanternCurrent: state.lanternFuel,
    lanternMax: state.maxLantern ?? 20,
    atk: state.attack,
    def: state.defense,
    burden: state.burden,
    lanternDrainModifier: state.lanternEfficiencyBps ?? 0,
    oilRestoreBase: supplyEffects.oilFuel,
    oilRestoreBonus,
    oilRestoreTotal: supplyEffects.oilFuel + oilRestoreBonus,
    torchRestoreBase: supplyEffects.torchFuel,
    torchRestoreBonus,
    torchRestoreTotal: supplyEffects.torchFuel + torchRestoreBonus,
    potionHealBase: supplyEffects.potionHeal,
    potionHealBonus,
    potionHealTotal: supplyEffects.potionHeal + potionHealBonus,
  };
}

function statBar(state: RunState): string {
  const stats = effectiveStats(state);
  const carriedItems = state.carriedItems.filter((i) => i.state === 'carried').length;
  const supplies = state.supplies ?? { oil: 0, torch: 0, potion: 0 };
  return `
    <div class="stats stats--hud">
      ${iconRow('HP', 'hp', `<strong class="${stats.hpCurrent <= 3 ? 'danger' : ''}">${esc(stats.hpCurrent)}</strong> / ${esc(stats.hpMax)}`)}
      ${iconRow('Lantern', 'lantern', `${esc(stats.lanternCurrent)} / ${esc(stats.lanternMax)} ${stats.lanternCurrent === 0 ? pill('dark!', 'down') : ''}`)}
      ${row('Oil / Torch / Potion', `${esc(supplies.oil)} / ${esc(supplies.torch)} / ${esc(supplies.potion)}`)}
      ${iconRow('Burden', 'burden', String(stats.burden))}
      ${iconRow('Carried Gold', 'gold', `<strong>${esc(state.carriedGold)}</strong>`)}
      ${row('Carried items', String(carriedItems))}
      ${row('ATK / DEF', `${esc(stats.atk)} / ${esc(stats.def)}`)}
      ${row('Floor / Turn', `${esc(state.floor)} / ${esc(state.turn)}`)}
      ${row('Type', pill(state.type, state.type === 'reward' ? 'ok' : 'muted'))}
    </div>`;
}

function percentFromBps(value: number): string {
  return (Math.abs(value) / 100).toFixed(1).replace(/\.0$/, '');
}

function currentStatsPanel(state: RunState): string {
  const stats = effectiveStats(state);
  const burdenReduction = state.burdenReduction ?? 0;
  const supplyEffectText = (total: number, unit: string, bonus: number) =>
    bonus !== 0 ? `+${total} ${unit} (+${bonus} gear)` : `+${total} ${unit}`;
  return `
    <section class="run__side-section run-info-panel">
      <h2>Current stats</h2>
      <div class="run-info-grid">
        ${row('HP', `${esc(stats.hpCurrent)} / ${esc(stats.hpMax)}`)}
        ${row('ATK', String(stats.atk))}
        ${row('DEF', String(stats.def))}
        ${row('Lantern', `${esc(stats.lanternCurrent)} / ${esc(stats.lanternMax)}`)}
        ${row('Drain modifier', stats.lanternDrainModifier > 0 ? `Lantern drain -${esc(percentFromBps(stats.lanternDrainModifier))}%` : 'Lantern drain 0%')}
        ${row('Oil effect', supplyEffectText(stats.oilRestoreTotal, 'fuel', stats.oilRestoreBonus))}
        ${row('Torch effect', supplyEffectText(stats.torchRestoreTotal, 'fuel', stats.torchRestoreBonus))}
        ${row('Potion effect', supplyEffectText(stats.potionHealTotal, 'HP', stats.potionHealBonus))}
        ${row('Burden', `load ${esc(stats.burden)} · Burden load -${esc(burdenReduction)}`)}
      </div>
    </section>`;
}

const EQUIP_SLOTS: EquipSlot[] = ['weapon', 'armor', 'lantern', 'trinket1', 'trinket2'];

function equippedGearPanel(state: AppState): string {
  const equipped = new Map<EquipSlot, InventoryItem>();
  for (const item of state.equipment?.items ?? []) {
    if (item.equippedSlot) equipped.set(item.equippedSlot, item);
  }
  const rows = EQUIP_SLOTS.map((slot) => {
    const item = equipped.get(slot);
    if (!item) return `<div class="run-gear-row"><strong>${esc(slotLabel(slot))}</strong><span class="muted">empty</span></div>`;
    const view = describeItem(item.item);
    return `
      <div class="run-gear-row">
        <strong>${esc(slotLabel(slot))}</strong>
        <span class="rarity rarity--${esc(item.item.rarity)}">${esc(view.title)}</span>
        <span class="muted">dura ${esc(view.durability)}</span>
        <span class="run-gear-row__effects">${esc(view.affixes.join(' · ') || 'no affixes')}</span>
      </div>`;
  }).join('');
  return `
    <section class="run__side-section run-info-panel">
      <h2>Equipped gear</h2>
      <div class="run-gear-list">${rows}</div>
    </section>`;
}

function itemSealText(item: RunFoundItem, floor: number): string {
  return floor >= item.sealDepth ? 'Would seal now' : `Needs floor ${item.sealDepth}+`;
}

function currentRunItemsPanel(state: RunState): string {
  const equippedTempIds = new Set(Object.values(state.foundEquippedSlots));
  const carried = state.carriedItems.filter((item) => item.state === 'carried');
  const foundHere = state.floorState.itemDrops
    .filter((drop) => drop.item.state === 'found' && drop.position.x === state.position.x && drop.position.y === state.position.y)
    .map((drop) => drop.item);
  const items = [...carried, ...foundHere];
  const body = items.length === 0
    ? '<p class="muted">No current-run items found yet.</p>'
    : `<div class="run-found-list">
        ${items.map((item) => {
          const view = describeRunItem(item);
          const equipped = equippedTempIds.has(item.tempId);
          const heldState = item.state === 'found' ? 'on tile' : 'carried';
          return `
            <div class="run-found-row">
              <strong>${itemIcon(item.slotType)}<span class="rarity rarity--${esc(item.rarity)}">${esc(view.title)}</span></strong>
              <span class="muted">${esc(heldState)} · ${esc(itemSealText(item, state.floor))}${equipped ? ' · equipped' : ''}</span>
              <span>${esc(view.affixes.join(' · ') || 'no affixes')}</span>
              ${item.state === 'carried'
                ? `<div class="btn-row">
                    ${equipped ? '' : btn('Equip (found)', 'act-equip-found', { item: item.tempId }, { variant: 'ghost' })}
                    ${btn('Discard', 'act-discard', { item: item.tempId }, { variant: 'ghost' })}
                  </div>`
                : ''}
            </div>`;
        }).join('')}
      </div>`;
  return `
    <section class="run__side-section run-info-panel">
      <h2>Current-run items</h2>
      ${body}
    </section>`;
}

function affixValue(affixes: unknown, stat: ItemAffix['stat']): number {
  if (!Array.isArray(affixes)) return 0;
  return affixes.reduce((sum, raw) => {
    const affix = raw as Partial<ItemAffix>;
    return affix.stat === stat && typeof affix.value === 'number' ? sum + affix.value : sum;
  }, 0);
}

function comparisonDeltaText(stat: ItemAffix['stat'], delta: number): string | null {
  if (delta === 0) return null;
  if (stat === 'lantern_efficiency_bps') {
    return delta > 0
      ? `Lantern drain -${percentFromBps(delta)}%`
      : `Lantern drain +${percentFromBps(delta)}%`;
  }
  if (stat === 'burden_reduction') return delta > 0 ? `Burden load -${delta}` : `Burden load +${Math.abs(delta)}`;
  return formatItemEffect({ stat, value: delta } as ItemAffix);
}

function equippedItemForSlot(state: AppState, run: RunState, slot: EquipSlot): InventoryItem | RunFoundItem | null {
  const tempId = run.foundEquippedSlots[slot];
  if (tempId) return run.carriedItems.find((item) => item.tempId === tempId && item.state === 'carried') ?? null;
  return state.equipment?.items.find((item) => item.equippedSlot === slot) ?? null;
}

function affixesForComparable(item: InventoryItem | RunFoundItem | null): unknown {
  if (!item) return [];
  return 'item' in item ? item.item.affixes : item.affixes;
}

function comparisonSlot(slotType: ItemSlotType): EquipSlot {
  return compatibleSlots(slotType)[0] ?? 'weapon';
}

function foundItemComparisonPanel(state: AppState, run: RunState): string {
  const pickupId = pickupHere(run);
  const found = pickupId
    ? run.floorState.itemDrops.find((drop) => drop.item.tempId === pickupId)?.item ?? null
    : null;
  if (!found) {
    return `
      <section class="run__side-section run-info-panel">
        <h2>Found vs equipped</h2>
        <p class="muted">Stand on an item to compare it with the matching equipped slot.</p>
      </section>`;
  }
  const slot = comparisonSlot(found.slotType);
  const equipped = equippedItemForSlot(state, run, slot);
  const stats: ItemAffix['stat'][] = [
    'attack',
    'defense',
    'max_hp',
    'max_lantern',
    'oil_restore_bonus',
    'torch_restore_bonus',
    'potion_heal_bonus',
    'lantern_efficiency_bps',
    'burden_reduction',
  ];
  const deltas = stats
    .map((stat) => comparisonDeltaText(stat, affixValue(found.affixes, stat) - affixValue(affixesForComparable(equipped), stat)))
    .filter((value): value is string => Boolean(value));
  const foundView = describeRunItem(found);
  const equippedLabel = equipped
    ? ('item' in equipped ? describeItem(equipped.item).title : describeRunItem(equipped).title)
    : `empty ${slotLabel(slot)}`;
  return `
    <section class="run__side-section run-info-panel">
      <h2>Found vs equipped</h2>
      <div class="run-compare">
        ${row('Found', `${esc(foundView.title)} · ${esc(affixSummary(found.affixes))}`)}
        ${row('Equipped', esc(equippedLabel))}
        ${row('Deltas', deltas.length ? esc(deltas.join(' · ')) : '<span class="muted">no stat change</span>')}
      </div>
    </section>`;
}

function movePad(state: RunState): string {
  const m = (dir: 'up' | 'down' | 'left' | 'right', label: string) =>
    btn(label, 'act-move', { dir }, { disabled: !canMove(state, dir) });
  return `
    <div class="movepad">
      <div></div>${m('up', '↑')}<div></div>
      ${m('left', '←')}${btn('•', 'act-wait', {}, { variant: 'ghost', title: 'wait (pass a turn)' })}${m('right', '→')}
      <div></div>${m('down', '↓')}<div></div>
    </div>`;
}

function contextActions(state: RunState): string {
  const buttons: string[] = [];
  const supplies = state.supplies ?? { oil: 0, torch: 0, potion: 0 };
  const stats = effectiveStats(state);
  if (canExtract(state)) buttons.push(btn('⛵ Extract (ferry)', 'act-extract', {}, { variant: 'primary' }));
  if (canDescend(state)) buttons.push(btn('▼ Descend', 'act-descend', {}, { variant: 'primary' }));
  const targets = adjacentEnemies(state);
  for (const id of targets) buttons.push(btn(`⚔ Attack ${id}`, 'act-attack', { target: id }, { variant: 'danger' }));
  if (targets.length === 0 && state.floorState.enemies.some((enemy) => enemy.hp > 0)) {
    buttons.push(btn('Attack unavailable: no adjacent enemy', 'act-attack-unavailable', {}, { variant: 'ghost', disabled: true }));
  }
  const pickupId = pickupHere(state);
  if (pickupId) buttons.push(btn('✋ Pick up item', 'act-pickup', { item: pickupId }));
  buttons.push(btn(`Oil x${supplies.oil} (+${stats.oilRestoreTotal} fuel)`, 'act-oil', {}, {
    variant: 'ghost',
    disabled: supplies.oil <= 0 || stats.lanternCurrent >= stats.lanternMax,
    title: stats.lanternCurrent >= stats.lanternMax ? 'Lantern fuel is already full' : undefined,
  }));
  buttons.push(btn(`Torch x${supplies.torch} (+${stats.torchRestoreTotal} fuel)`, 'act-torch', {}, {
    variant: 'ghost',
    disabled: supplies.torch <= 0 || stats.lanternCurrent >= stats.lanternMax,
    title: stats.lanternCurrent >= stats.lanternMax ? 'Lantern fuel is already full' : undefined,
  }));
  buttons.push(btn(`Potion x${supplies.potion} (+${stats.potionHealTotal} HP)`, 'act-potion', {}, {
    variant: 'ghost',
    disabled: supplies.potion <= 0 || stats.hpCurrent >= stats.hpMax,
    title: stats.hpCurrent >= stats.hpMax ? 'HP is already full' : undefined,
  }));
  const shallowExtractionHint = state.floor < 3
    ? '<p class="hint">Extraction begins at floor 3. Loot found before floor 3 can still be sealed if you survive to a floor 3+ ferry-step.</p>'
    : '';
  return `<div class="btn-row run__context-actions">${buttons.join('')}</div>${shallowExtractionHint}`;
}

function enemyRoster(state: RunState): string {
  const active = state.floorState.enemies.filter((enemy) => enemy.hp > 0);
  if (active.length === 0) return '<p class="muted">No active enemies on this floor.</p>';
  return `
    <div class="enemy-list">
      ${active.map((enemy) => `
        <div class="enemy-row">
          <strong>${esc(enemy.id)}</strong>
          <span>HP ${esc(enemy.hp)}</span>
          <span>DMG ${esc(enemy.damage)}</span>
          <code>${esc(enemy.position.x)},${esc(enemy.position.y)}</code>
        </div>`).join('')}
    </div>`;
}

function eventText(event: EngineEvent): string {
  switch (event.type) {
    case 'supply_used': {
      const names: Record<SupplyKey, string> = {
        oil: 'Oil Flask',
        torch: 'Torch Bundle',
        potion: 'Healing Draught',
      };
      const stat = event.stat === 'lantern' ? 'Lantern' : 'HP';
      const modifiedAmount = event.modifiedAmount ?? event.configuredAmount;
      const capped = event.capped ? `, capped at max ${stat}` : '';
      const gearText = modifiedAmount !== event.configuredAmount
        ? ` Configured +${event.configuredAmount}; gear-modified +${modifiedAmount}.`
        : '';
      const afterTurn = event.afterTurn ?? event.after;
      const flow = afterTurn !== event.after
        ? ` ${stat} ${event.before} → ${event.after}; after response ${afterTurn}.`
        : ` ${stat} ${event.before} → ${event.after}.`;
      return `${names[event.supplyKey]} restored +${event.actualAppliedAmount} ${stat}${capped}.${gearText}${flow}`;
    }
    case 'enemy_damaged':
      return event.killed
        ? `${event.enemyId} killed (${event.damage} damage)`
        : `${event.enemyId} hit for ${event.damage} (HP ${event.hp})`;
    case 'enemy_moved':
      return `${event.enemyId} moved ${event.from.x},${event.from.y} → ${event.to.x},${event.to.y}`;
    case 'player_damaged':
      return event.source === 'enemy'
        ? `Enemy hit you for ${event.damage}. ${event.enemyId ? `${event.enemyId} ` : ''}(HP ${event.hp})`
        : `Player took ${event.damage} from the dark (HP ${event.hp})`;
  }
}

function eventLog(events: EngineEvent[]): string {
  if (events.length === 0) {
    return '<div class="combat-log combat-log--empty"><div>No combat events on the last action.</div></div>';
  }
  return `
    <div class="combat-log">
      ${events.map((event) => `<div>${esc(eventText(event))}</div>`).join('')}
    </div>`;
}

function actionFeedback(state: AppState): string {
  const outcome = state.lastOutcome ? `last: ${esc(state.lastOutcome.kind)}` : 'last: none';
  return `
    <div class="run__action-feedback" aria-live="polite" aria-atomic="true">
      <p class="hint">${outcome}</p>
      ${eventLog(state.lastEvents)}
    </div>`;
}

function terminalOverlay(terminal: TerminalResult): string {
  let body = '';
  if (terminal.kind === 'extracted') {
    const echoNotice = terminal.practiceOnly
      ? `<p class="muted">${esc(terminal.message ?? 'Echo run: practice only. Loot is not banked.')}</p><p class="muted">Practice only. Loot and Gold disappear when the Echo ends.</p>`
      : '';
    const sealed = terminal.sealed.length
      ? terminal.sealed.map((s) => `<li><span class="rarity rarity--${esc(s.rarity)}">${esc(s.rarity)} ${esc(s.slotType)}</span> → sealed (id ${esc(s.itemInstanceId.slice(0, 8))})</li>`).join('')
      : '<li class="muted">none</li>';
    const left = terminal.leftBehind.length
      ? terminal.leftBehind.map((l) => `<li><span class="rarity rarity--${esc(l.rarity)}">${esc(l.rarity)} ${esc(l.slotType)}</span> — left behind (${esc(l.reason)}; needs floor ≥ ${esc(l.sealDepth)})</li>`).join('')
      : '<li class="muted">none</li>';
    body = `
      <h2 class="win">⛵ Extracted</h2>
      ${echoNotice}
      ${row('Gross carried Gold', String(terminal.settlement.grossCarriedGold))}
      ${row('Toll', String(terminal.settlement.toll))}
      ${row('Banked (net)', `<strong>${esc(terminal.settlement.netBankedGold)}</strong>`)}
      <h3>Sealed</h3><ul>${sealed}</ul>
      <h3>Left behind</h3><ul>${left}</ul>`;
  } else if (terminal.kind === 'died') {
    body = `
      <h2 class="dead">☠ Died</h2>
      ${row('Cause', terminal.cause ?? 'unknown')}
      ${row('Floor', String(terminal.floor))}
      ${row('Carried Gold lost', String(terminal.carriedGoldLost))}
      <p class="muted">${terminal.practiceOnly ? 'Practice only. Loot and Gold disappear when the Echo ends.' : 'Carried Gold and unsealed loot are lost on death.'}</p>`;
  } else {
    body = `
      <h2 class="dead">Abandoned</h2>
      ${row('Floor', String(terminal.floor))}
      ${row('Carried Gold lost', String(terminal.carriedGoldLost))}
      <p class="muted">${terminal.practiceOnly ? 'Practice only. Loot and Gold disappear when the Echo ends.' : 'Abandoning settles nothing and refunds no Pass.'}</p>`;
  }
  return `
    <div class="overlay">
      <div class="overlay__card">
        ${body}
        <div class="btn-row">${btn('Back to Waystation', 'dismiss-terminal', {}, { variant: 'primary' })}</div>
      </div>
    </div>`;
}

function activeRunHtml(state: AppState, run: RunState): string {
  if (!run) {
    return `<section class="card"><h2>No active run</h2><div class="btn-row">${btn('Back to dashboard', 'goto-dashboard', {}, { variant: 'ghost' })}</div></section>`;
  }
  return `
    <div class="run">
      <section class="run__primary ui-panel ui-panel--stage">
        <div class="run__head run__head--with-legend">
          <h2>Descent — floor ${esc(run.floor)}</h2>
          ${runLegendHtml()}
          ${btn('Abandon', 'abandon-run', {}, { variant: 'danger' })}
        </div>
        ${statBar(run)}
        <div class="run__stage-panel">${gameFeelStageHtml()}</div>
        <div class="run__bottom-grid">
          ${equippedGearPanel(state)}
          ${currentRunItemsPanel(run)}
          ${foundItemComparisonPanel(state, run)}
        </div>
      </section>
      <aside class="run__side ui-panel ui-panel--compact">
        <section class="run__side-section run__actions">
          <h2>Actions</h2>
          <div class="run__controls">
            ${movePad(run)}
            <div>
              ${contextActions(run)}
              ${actionFeedback(state)}
            </div>
          </div>
        </section>
        ${currentStatsPanel(run)}
        <section class="run__side-section">
          <h2>Enemies</h2>
          ${enemyRoster(run)}
        </section>
        <section class="run__side-section run__debug">
          <h2>Debug</h2>
          ${debugPanelsHtml(run, state.devMode)}
        </section>
      </aside>
    </div>`;
}

export function renderRun(state: AppState): string {
  const run = state.run;
  if (!run) {
    if (state.terminal) return terminalOverlay(state.terminal);
    return `<section class="card"><h2>No active run</h2><div class="btn-row">${btn('Back to dashboard', 'goto-dashboard', {}, { variant: 'ghost' })}</div></section>`;
  }
  const body = activeRunHtml(state, run);
  return state.terminal ? body + terminalOverlay(state.terminal) : body;
}
